import { adminApi } from "../../api";
import { Accordion, LoadingText, Modal } from "../ui";
import { AssistantContent } from "../Chat";
import { useAsync } from "../../hooks";
import AuditActionBadge from "./AuditActionBadge";
import { formatDateTime } from "../../utils";
import type { AuditLogDetail, AuditQuery } from "../../types/audit";

interface AuditLogDetailModalProps {
  entryId: string;
  onClose: () => void;
}

function cellValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function DataTable({ rows }: { rows: Record<string, unknown>[] | undefined }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-gray-400">No rows returned.</p>;
  }
  const columns = Object.keys(rows[0]);
  return (
    <div className="max-h-64 overflow-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-[11px]">
        <thead className="sticky top-0 bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th key={column} className="whitespace-nowrap px-3 py-1.5 text-left font-semibold text-gray-600">{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {columns.map((column) => (
                <td key={column} className="whitespace-nowrap px-3 py-1 text-gray-700">{cellValue(row[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const HANDLED_META_KEYS = new Set(["question", "answer", "queries", "data_truncated", "message_id"]);

export default function AuditLogDetailModal({ entryId, onClose }: AuditLogDetailModalProps) {
  const { data: entry, loading, error } = useAsync<AuditLogDetail>(
    () => adminApi.getAuditLogDetail(entryId).then((response) => response.data),
    [entryId],
    { errorMessage: "Failed to load entry." },
  );

  const meta = entry?.metadata_ ?? {};
  const queries: AuditQuery[] = Array.isArray(meta.queries) ? meta.queries : [];
  const otherMeta = Object.fromEntries(
    Object.entries(meta).filter(([key]) => !HANDLED_META_KEYS.has(key)),
  );

  return (
    <Modal
      onClose={onClose}
      size="xl"
      title="Audit entry"
      headerAccessory={entry && <AuditActionBadge action={entry.action} />}
      subtitle={entry ? formatDateTime(entry.created_at) : undefined}
    >
      {loading ? (
        <LoadingText />
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : entry ? (
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-gray-50 p-3 ring-1 ring-gray-100">
            <p className="text-xs font-medium text-gray-500">User</p>
            <p className="text-gray-800">{entry.user_name || "—"}</p>
            {entry.user_email && <p className="text-xs text-gray-400">{entry.user_email}</p>}
          </div>

          {meta.question && (
            <Accordion title="Question" defaultOpen>
              <p className="whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-800 ring-1 ring-gray-100">
                {meta.question}
              </p>
            </Accordion>
          )}

          {meta.answer && (
            <Accordion title="Brian answer" defaultOpen>
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-800 ring-1 ring-gray-100">
                <AssistantContent content={meta.answer} actionsMode="hidden" />
              </div>
            </Accordion>
          )}

          {queries.length > 0 && (
            <Accordion title="SQL &amp; data returned">
              <div className="space-y-3">
                {queries.map((query, queryIndex) => (
                  <div key={queryIndex} className="rounded-lg border border-gray-200 p-3">
                    {query.sql && (
                      <pre className="mb-2 overflow-x-auto whitespace-pre-wrap rounded bg-gray-900 p-2 font-mono text-[11px] text-gray-100">{query.sql}</pre>
                    )}
                    {query.error ? (
                      <p className="text-xs text-red-500">Error: {query.error}</p>
                    ) : (
                      <>
                        <p className="mb-1 text-xs text-gray-500">{query.row_count} row(s) returned</p>
                        <DataTable rows={query.rows} />
                      </>
                    )}
                  </div>
                ))}
              </div>
              {meta.data_truncated && (
                <p className="mt-2 text-xs text-amber-600">
                  Data sample truncated — only a capped portion of the returned rows is stored.
                </p>
              )}
            </Accordion>
          )}

          {Object.keys(otherMeta).length > 0 && (
            <Accordion title="Details">
              <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 font-mono text-[11px] text-gray-700 ring-1 ring-gray-100">
                {JSON.stringify(otherMeta, null, 2)}
              </pre>
            </Accordion>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
